import { environment } from '../environments/environment';

/**
 * Runtime settings of the portal. The two placeholders are substituted by update-version.ps1
 * during the build; without that step the raw tokens ship as the version, which is why the
 * version is shown in the user menu.
 */
interface AppSettings {
  version: string;
  release: string;
  stage: string;
  isProduction: boolean;
  authentication: {
    authority: string;
    clientId: string;
    scope: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
    requireHttps: boolean;
  };
  endpoints: {
    accounts: AccountsEndpoints;
    masterdata: {
      enumValues: string;
      countries: string;
      federalStates: string;
    };
  };
}

/**
 * Routes are taken from the Accounts API -- **never the `/core/` ones**, which belong to
 * `UI.Angular.Core` and are the tenant-spanning administration.
 *
 * Every entry ends with a slash where something is appended to it, and carries `:tenantId` where
 * the tenant sits inside the path rather than at the end. Substituting it is the client's job; the
 * settings stay data, so they can be read in a test without a running application.
 */
interface AccountsEndpoints {
  /** POST `search`, GET/PUT/DELETE `{id}`, POST `invite`, GET `configuration/validation`. */
  systemUsers: string;
  /**
   * POST `search`, DELETE `{id}`.
   *
   * There is no read of a single invitation and **no resend** -- the only two endpoints are the two
   * named here, so the screen offers exactly one action.
   */
  invitations: string;
  /** GET -- the roles assignable to a system user. */
  systemUserRoles: string;
  /** GET -- the subscriptions assignable to a system user. */
  systemUserSubscriptions: string;
  /**
   * POST `search` and GET `configuration/validation/type/{n}`.
   *
   * Reading and writing sit on different paths: the tenant appears in the path only for the write
   * operations, which is why `subscriptionsOfTenant` exists beside this.
   */
  subscriptions: string;
  /** POST create, GET/PUT/DELETE `{id}`, POST `unique/name`, POST `unique/subscriptionkey`. */
  subscriptionsOfTenant: string;
  /** POST create and `search`, GET/PUT/DELETE `{id}`, POST `unique/name`, GET the configuration. */
  products: string;
  /**
   * POST create and `search`, GET/PUT/DELETE `{id}`, POST `unique/name` and `unique/clientid`,
   * GET `{id}/regenerate/clientsecret` and the configurations.
   */
  applications: string;
  /**
   * POST `search` -- flat, across every application the caller may see.
   *
   * Writing sits under the application instead, which is why `machineTokensOfApplication` exists
   * beside this: the same split as reading and writing a subscription.
   */
  machineTokens: string;
  /** POST create, GET/PUT/DELETE `{id}`, GET `{id}/regenerate/clientsecret`. Carries `:applicationId`. */
  machineTokensOfApplication: string;
  /** POST `search`, `subscribe` and `unsubscribe`, GET the configuration. There is no update. */
  serviceHooks: string;
  /**
   * POST create and `search`, GET/PUT/DELETE `{id}`, POST `unique/name`, `unique/displayname` and
   * `unique/identifier`, GET the configurations.
   *
   * **The base tool of the whole area.** Modules, sub-modules and components each point at exactly
   * one permission; a permission points at nothing and works without any of them.
   */
  permissions: string;
  /** POST create and `search`, GET/PUT/DELETE `{id}`, GET the configurations. */
  modules: string;
  /** POST create, GET/PUT/DELETE `{subModuleId}`. Carries `:moduleId`. */
  subModulesOfModule: string;
  /** POST `search` -- flat across the application, which is what builds the tree. */
  subModules: string;
  /** POST create, GET/PUT/DELETE `{componentId}`. Carries `:moduleId` and `:subModuleId`. */
  componentsOfSubModule: string;
  /** POST `search`, GET the configurations. */
  components: string;
  /**
   * POST `search` -- a component's additional permissions, flat across the application.
   *
   * **Search only.** They are written with their component and have no route of their own; this one
   * exists so the permissions area can see them without opening every component.
   */
  componentPermissions: string;
  /** POST create and `search`, GET/PUT/DELETE `{id}`, GET the configurations. */
  roleTemplates: string;
  /**
   * POST create and `search`, GET/PUT/DELETE `{id}`, POST `unique/name`, GET the configurations.
   *
   * **The bracket around the licences**, and a licence cannot be moved between two of them: the
   * licence update carries no `LicenseTypeId`.
   */
  licenseTypes: string;
  /** POST create and `search`, GET/PUT/DELETE `{id}`, POST `unique/name`, GET the configurations. */
  licenses: string;
  /**
   * POST `search` -- the permissions of **one** licence, with the whole path by name.
   *
   * Carries `:licenseId`. There is no search across licences, so the third level of the outline is
   * one request per licence opened.
   */
  licensePermissionsOfLicense: string;
  /**
   * GET -- the permissions of one application as the nested structure, names and identifiers only.
   *
   * **For picking, not for editing.** It drops modules that have no sub-module and can be narrowed to
   * an account's licences, so a module created a moment ago is not in it. The structure screen builds
   * its tree from the four searches instead. Carries `:applicationId`.
   */
  permissionTree: string;
  /** POST -- carries the changes of licences and role templates into the application accounts. */
  licenseAndRoleSynchronization: string;
  /** POST -- the tenants the signed-in user may act in. Id and display name only. */
  tenantFilter: string;
  /** GET -- the users of one tenant, assignable to a subscription. Carries `:tenantId`. */
  tenantUsers: string;
  /**
   * POST create and `search`, GET/DELETE `{id}`, PUT `{id}/common` and `{id}/settings`,
   * POST `unique/name`, `unique/subdomain` and `unique/externalaccountid`.
   *
   * **Two updates rather than one**, and that is the contract rather than a screen decision: the
   * customer's own data and the technical settings are written separately, and each has its own
   * validation configuration.
   */
  applicationAccounts: string;
  /** POST `search` -- the roles of the accounts, flat. Writing sits under the account. */
  applicationAccountRoles: string;
  /** POST create, GET/PUT/DELETE `{roleId}`, GET the configurations. Carries `:accountId`. */
  rolesOfAccount: string;
  /** POST `search` -- the API tokens of the accounts, flat, and the configurations. Writing sits under the account. */
  applicationAccountApiTokens: string;
  /**
   * POST create, GET/PUT/DELETE `{apiTokenId}`, POST `{apiTokenId}/regeneratesecret`, PUT
   * `{apiTokenId}/lock` and `{apiTokenId}/unlock`. Carries `:accountId`.
   */
  apiTokensOfAccount: string;
  /** POST `search` -- every user of every account, and `unique/externaluserid`. */
  accountUsers: string;
  /** POST `invite`, GET/PUT/DELETE `{userId}`, GET the configurations. Carries `:accountId`. */
  usersOfAccount: string;
  /** GET -- the account's logo as a file. Carries `:accountId`. */
  companyLogoOfAccount: string;
  /**
   * GET -- the permission tree **limited to what the account's licences allow**.
   *
   * The domain rejects a role permission outside that set, so this is what the role picker offers
   * from. Carries `:applicationId` and `:accountId`.
   */
  permissionTreeOfAccount: string;
}

const accountsUrl = environment.baseUrls.accountsUrl;

export const appSettings: AppSettings = {
  version: '1.0.0.39',
  release: 'Release 30.09.2026',
  stage: environment.stage,
  isProduction: environment.production,
  authentication: {
    authority: environment.oidc.authority,
    clientId: environment.oidc.clientId,
    scope: environment.oidc.scope,
    redirectUri: environment.baseUrls.uiUrl,
    postLogoutRedirectUri: environment.baseUrls.uiUrl,
    requireHttps: environment.oidc.requireHttps
  },
  endpoints: {
    accounts: {
      systemUsers: accountsUrl + '/accounts/users/',
      invitations: accountsUrl + '/accounts/users/invitations/',
      systemUserRoles: accountsUrl + '/accounts/roles',
      systemUserSubscriptions: accountsUrl + '/accounts/subscriptions',
      subscriptions: accountsUrl + '/tenants/subscriptions/',
      subscriptionsOfTenant: accountsUrl + '/tenants/:tenantId/subscriptions/',
      products: accountsUrl + '/applications/types/',
      applications: accountsUrl + '/applications/',
      machineTokens: accountsUrl + '/applications/machinetokens/',
      machineTokensOfApplication: accountsUrl + '/applications/:applicationId/machinetokens/',
      serviceHooks: accountsUrl + '/servicehooks/',
      permissions: accountsUrl + '/permissions/',
      modules: accountsUrl + '/permissions/modules/',
      subModulesOfModule: accountsUrl + '/permissions/modules/:moduleId/submodules/',
      subModules: accountsUrl + '/permissions/modules/submodules/',
      componentsOfSubModule: accountsUrl + '/permissions/modules/:moduleId/submodules/:subModuleId/components/',
      components: accountsUrl + '/permissions/modules/submodules/components/',
      componentPermissions: accountsUrl + '/permissions/modules/submodules/components/permissions/',
      roleTemplates: accountsUrl + '/permissions/roletemplates/',
      licenseTypes: accountsUrl + '/permissions/licensetypes/',
      licenses: accountsUrl + '/permissions/licenses/',
      licensePermissionsOfLicense: accountsUrl + '/permissions/licenses/:licenseId/permissions/',
      permissionTree: accountsUrl + '/permissions/tree/application/:applicationId',
      licenseAndRoleSynchronization: accountsUrl + '/permissions/synchronizations/license-and-role',
      tenantFilter: accountsUrl + '/tenants/filter',
      tenantUsers: accountsUrl + '/tenants/:tenantId/users/search',
      /* The customers area. An application account is a customer of the vendor; its roles and its
         users hang underneath it, and the users additionally have a list across all accounts. */
      applicationAccounts: accountsUrl + '/applications/accounts/',
      applicationAccountRoles: accountsUrl + '/applications/accounts/roles/',
      rolesOfAccount: accountsUrl + '/applications/accounts/:accountId/roles/',
      applicationAccountApiTokens: accountsUrl + '/applications/accounts/apitokens/',
      apiTokensOfAccount: accountsUrl + '/applications/accounts/:accountId/apitokens/',
      accountUsers: accountsUrl + '/applications/accounts/users/',
      usersOfAccount: accountsUrl + '/applications/accounts/:accountId/users/',
      companyLogoOfAccount: accountsUrl + '/applications/accounts/:accountId/companylogo',
      /* The permission tree an account may pick from: what its licences allow, and nothing more. */
      permissionTreeOfAccount: accountsUrl + '/permissions/tree/application/:applicationId/account/:accountId'
    },
    masterdata: {
      enumValues: accountsUrl + '/masterdata/enumvalues/',
      countries: accountsUrl + '/masterdata/countries/',
      federalStates: accountsUrl + '/masterdata/federalstates/'
    }
  }
};
